// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { buildCaseAddressLegendAnalysis, normalizeCaseAddressLegendAddressKey } from './CaseBuddyAddressLegend.js';
import { caseBuddyHashString, firstNonEmptyValue, normalizeCell, normalizeComparisonKey, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { getCaseTemplateLegendEntries } from './CaseBuddyTemplateDeliverable.js';
import { formatCaseWwwUsd } from './CaseBuddyWhatWentWhere.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_CONCLUSION_RENDERER_VERSION = 'case_conclusion_renderer_web_v2_desktop_style';

export const CASE_CONCLUSION_CATEGORY_EXCHANGE = 'exchange';
export const CASE_CONCLUSION_CATEGORY_NON_EXCHANGE = 'non_exchange_entity';
export const CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET = 'private_wallet';
export const CASE_CONCLUSION_CATEGORY_UNKNOWN = 'unknown';
export const CASE_CONCLUSION_CATEGORY_DORMANT = 'dormant';

const CASE_CONCLUSION_CATEGORY_DISPLAY = Object.freeze({
  [CASE_CONCLUSION_CATEGORY_EXCHANGE]: 'Exchange',
  [CASE_CONCLUSION_CATEGORY_NON_EXCHANGE]: 'Non-Exchange Entity',
  [CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET]: 'Private Wallet',
  [CASE_CONCLUSION_CATEGORY_UNKNOWN]: 'Unknown / Unattributed Endpoint',
  [CASE_CONCLUSION_CATEGORY_DORMANT]: 'Dormant Wallet'
});

export function caseConclusionFormatList(values = [], maxItems = 3) {
  const items = (Array.isArray(values) ? values : []).map(value => normalizeCell(value)).filter(Boolean).slice(0, maxItems);
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

export function caseConclusionFormatUsd(value = 0) {
  const formatted = formatCaseWwwUsd(Number(value));
  return formatted ? `${formatted} USDT` : '';
}

export function caseConclusionFormatPercent(ratio = 0) {
  const numeric = Number(ratio);
  const percent = Number.isFinite(numeric) ? numeric * 100 : 0;
  return `${percent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export function caseConclusionJoinHumanList(values = []) {
  const items = (Array.isArray(values) ? values : []).map(value => normalizeCell(value)).filter(Boolean);
  if (items.length <= 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

export function caseConclusionNormalizeCategoryTag(category = '') {
  const key = normalizeComparisonKey(category);
  const compactKey = key.replace(/[^a-z0-9]/g, '');
  if (compactKey === 'exchange' || key === CASE_CONCLUSION_CATEGORY_EXCHANGE) return CASE_CONCLUSION_CATEGORY_EXCHANGE;
  if (compactKey === 'nonexchange' || compactKey === 'nonexchangeentity' || key === CASE_CONCLUSION_CATEGORY_NON_EXCHANGE) return CASE_CONCLUSION_CATEGORY_NON_EXCHANGE;
  if (compactKey === 'dormantwallet' || compactKey === 'dormantwallets' || key === CASE_CONCLUSION_CATEGORY_DORMANT) return CASE_CONCLUSION_CATEGORY_DORMANT;
  if (compactKey === 'unknown' || compactKey === 'unknownunattributedendpoint' || compactKey === 'lostdismissed' || key === CASE_CONCLUSION_CATEGORY_UNKNOWN) return CASE_CONCLUSION_CATEGORY_UNKNOWN;
  if (compactKey === 'other' || compactKey === 'privatewallet' || compactKey === 'privatewallets' || key === CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET) return CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET;
  return '';
}

export function caseConclusionDisplayCategory(categoryTag = '') {
  return CASE_CONCLUSION_CATEGORY_DISPLAY[categoryTag] || normalizeCell(categoryTag) || 'Other';
}

export function caseConclusionTopEntities(whatWentWhereState = {}, category = '', maxItems = 3) {
  const requestedTag = caseConclusionNormalizeCategoryTag(category);
  return (Array.isArray(whatWentWhereState.entitySummaryRows) ? whatWentWhereState.entitySummaryRows : [])
    .filter(row => caseConclusionNormalizeCategoryTag(row.category) === requestedTag && normalizeCell(row.entity))
    .sort((a, b) => (Number(b.usdTotal) || 0) - (Number(a.usdTotal) || 0) || normalizeCell(a.entity).localeCompare(normalizeCell(b.entity)))
    .slice(0, maxItems)
    .map(row => `${row.entity}${row.usdDisplay ? ` (${row.usdDisplay})` : ''}`);
}

function caseConclusionBucketNameForCategory(categoryTag = '', sourceName = '') {
  const name = normalizeCell(sourceName);
  if (name) return name;
  if (categoryTag === CASE_CONCLUSION_CATEGORY_DORMANT) return 'Dormant Wallets';
  if (categoryTag === CASE_CONCLUSION_CATEGORY_UNKNOWN) return 'Unknown / Needs Further Investigation';
  if (categoryTag === CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET) return 'Private Wallet Addresses';
  if (categoryTag === CASE_CONCLUSION_CATEGORY_EXCHANGE) return 'Exchanges';
  if (categoryTag === CASE_CONCLUSION_CATEGORY_NON_EXCHANGE) return 'Non-Exchange Entities';
  return 'Other';
}

function caseConclusionBucketSort(left = {}, right = {}) {
  return (Number(right.usdEquivalent) || 0) - (Number(left.usdEquivalent) || 0) ||
    normalizeCell(left.name).localeCompare(normalizeCell(right.name), undefined, { sensitivity: 'base' });
}

function caseConclusionBuildEntityBuckets(whatWentWhereState = {}) {
  const buckets = [];
  const rows = Array.isArray(whatWentWhereState.entitySummaryRows) ? whatWentWhereState.entitySummaryRows : [];
  rows.forEach((row, index) => {
    const categoryTag = caseConclusionNormalizeCategoryTag(row.category);
    if (categoryTag !== CASE_CONCLUSION_CATEGORY_EXCHANGE && categoryTag !== CASE_CONCLUSION_CATEGORY_NON_EXCHANGE) return;
    const usdEquivalent = Number(row.usdTotal) || 0;
    if (usdEquivalent <= 0) return;
    const name = caseConclusionBucketNameForCategory(categoryTag, row.entity);
    buckets.push({
      key: `entity-${categoryTag}-${caseBuddyHashString(`${name}|${index}`)}`,
      categoryTag,
      category: caseConclusionDisplayCategory(categoryTag),
      displayCategory: caseConclusionDisplayCategory(categoryTag),
      name,
      usdEquivalent,
      usdTotal: usdEquivalent,
      usdDisplay: caseConclusionFormatUsd(usdEquivalent),
      percentageOfTraced: 0,
      percentageDisplay: '0.00%',
      tokensText: normalizeCell(row.tokensText),
      unresolvedCount: Number(row.unresolvedCount) || 0,
      searchText: [categoryTag, name, row.tokensText, row.unresolvedCount].map(value => normalizeCell(value).toLowerCase()).join(' ')
    });
  });
  return buckets;
}

function caseConclusionCountDistinctPathBuckets(whatWentWhereState = {}, categoryTag = '') {
  const rows = Array.isArray(whatWentWhereState.pathEndingRows) ? whatWentWhereState.pathEndingRows : [];
  const seen = new Set();
  rows.forEach((row) => {
    if (caseConclusionNormalizeCategoryTag(row.category) !== categoryTag) return;
    const key = normalizeComparisonKey(row.entity) || normalizeComparisonKey(row.toAddress) || normalizeComparisonKey(row.pathLabel) || normalizeComparisonKey(row.transactionHash);
    if (key) seen.add(key);
  });
  return seen.size;
}

function caseConclusionBuildAggregateBuckets(whatWentWhereState = {}, existingBuckets = []) {
  const buckets = [];
  const represented = new Set((Array.isArray(existingBuckets) ? existingBuckets : []).map(bucket => bucket.categoryTag));
  const rows = Array.isArray(whatWentWhereState.categorySummaryRows) ? whatWentWhereState.categorySummaryRows : [];
  rows.forEach((row, index) => {
    const categoryTag = caseConclusionNormalizeCategoryTag(row.category);
    const usdEquivalent = Number(row.usdTotal) || 0;
    if (!categoryTag || usdEquivalent <= 0) return;
    if ((categoryTag === CASE_CONCLUSION_CATEGORY_EXCHANGE || categoryTag === CASE_CONCLUSION_CATEGORY_NON_EXCHANGE) && represented.has(categoryTag)) return;
    let name = '';
    if (categoryTag === CASE_CONCLUSION_CATEGORY_DORMANT) name = 'Dormant Wallets';
    else if (categoryTag === CASE_CONCLUSION_CATEGORY_UNKNOWN) name = normalizeComparisonKey(row.category) === 'lostdismissed' ? 'Lost / Dismissed Paths' : 'Unknown / Needs Further Investigation';
    else if (categoryTag === CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET) name = 'Private Wallet Addresses';
    else name = caseConclusionBucketNameForCategory(categoryTag, row.category);
    buckets.push({
      key: `aggregate-${categoryTag}-${caseBuddyHashString(`${name}|${index}`)}`,
      categoryTag,
      category: caseConclusionDisplayCategory(categoryTag),
      displayCategory: caseConclusionDisplayCategory(categoryTag),
      name,
      usdEquivalent,
      usdTotal: usdEquivalent,
      usdDisplay: caseConclusionFormatUsd(usdEquivalent),
      percentageOfTraced: 0,
      percentageDisplay: '0.00%',
      tokensText: normalizeCell(row.tokensText),
      unresolvedCount: Number(row.unresolvedCount) || 0,
      searchText: [categoryTag, name, row.tokensText, row.unresolvedCount].map(value => normalizeCell(value).toLowerCase()).join(' ')
    });
  });
  return buckets;
}

export function buildCaseConclusionBuckets(whatWentWhereState = {}) {
  const entityBuckets = caseConclusionBuildEntityBuckets(whatWentWhereState);
  const aggregateBuckets = caseConclusionBuildAggregateBuckets(whatWentWhereState, entityBuckets);
  return [...entityBuckets, ...aggregateBuckets]
    .filter(bucket => (Number(bucket.usdEquivalent) || 0) > 0)
    .sort(caseConclusionBucketSort);
}

function caseConclusionSumBuckets(buckets = [], categoryTag = '') {
  return (Array.isArray(buckets) ? buckets : [])
    .filter(bucket => bucket.categoryTag === categoryTag)
    .reduce((sum, bucket) => sum + (Number(bucket.usdEquivalent) || 0), 0);
}

function caseConclusionCountBuckets(buckets = [], categoryTag = '') {
  return (Array.isArray(buckets) ? buckets : []).filter(bucket => bucket.categoryTag === categoryTag && (Number(bucket.usdEquivalent) || 0) > 0).length;
}

function caseConclusionDetermineMajorityCategory(state = {}) {
  const total = Number(state.totalTracedUsd) || 0;
  if (total <= 0) return '';
  const pairs = [
    [CASE_CONCLUSION_CATEGORY_EXCHANGE, state.exchangeUsd],
    [CASE_CONCLUSION_CATEGORY_NON_EXCHANGE, state.nonExchangeUsd],
    [CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET, state.privateWalletUsd],
    [CASE_CONCLUSION_CATEGORY_UNKNOWN, state.unknownUsd],
    [CASE_CONCLUSION_CATEGORY_DORMANT, state.dormantUsd]
  ].sort((left, right) => (Number(right[1]) || 0) - (Number(left[1]) || 0));
  if (!pairs.length || (Number(pairs[0][1]) || 0) <= 0) return '';
  return ((Number(pairs[0][1]) || 0) / total) >= 0.5 ? pairs[0][0] : '';
}

function caseConclusionBuildCategoryPhrases(state = {}, skipCategory = '') {
  const phrases = [];
  if (skipCategory !== CASE_CONCLUSION_CATEGORY_EXCHANGE && (state.exchangeBucketCount || 0) > 0) {
    phrases.push(state.exchangeBucketCount === 1 ? '1 exchange' : `${state.exchangeBucketCount} exchanges`);
  }
  if (skipCategory !== CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET && (state.privateWalletBucketCount || 0) > 0) {
    phrases.push(state.privateWalletBucketCount === 1 ? 'a private wallet address' : 'some private wallet addresses');
  }
  if (skipCategory !== CASE_CONCLUSION_CATEGORY_NON_EXCHANGE && (state.nonExchangeEntityBucketCount || 0) > 0) {
    phrases.push(state.nonExchangeEntityBucketCount === 1 ? '1 non-exchange entity' : `${state.nonExchangeEntityBucketCount} non-exchange entities`);
  }
  if (skipCategory !== CASE_CONCLUSION_CATEGORY_UNKNOWN && (state.unknownBucketCount || 0) > 0) {
    phrases.push('some presently unattributed service-style endpoints');
  }
  if (skipCategory !== CASE_CONCLUSION_CATEGORY_DORMANT && (Number(state.dormantUsd) || 0) > 0) {
    phrases.push('dormant wallets');
  }
  return phrases;
}

function caseConclusionBuildTailPhrase(phrases = []) {
  const text = caseConclusionJoinHumanList(phrases);
  return text ? `, as well as ${text}` : '';
}

function caseConclusionBuildLeadSentence(state = {}) {
  const anchorValue = (Number(state.totalEstimatedLossUsd) || 0) > 0 ? Number(state.totalEstimatedLossUsd) : Number(state.totalTracedUsd) || 0;
  const majority = caseConclusionDetermineMajorityCategory(state);
  const others = caseConclusionBuildCategoryPhrases(state, majority);
  if (majority === CASE_CONCLUSION_CATEGORY_EXCHANGE) {
    return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was mostly traced to exchanges${caseConclusionBuildTailPhrase(others)}.`;
  }
  if (majority === CASE_CONCLUSION_CATEGORY_NON_EXCHANGE) {
    return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was largely traced to non-exchange entities${caseConclusionBuildTailPhrase(others)}.`;
  }
  if (majority === CASE_CONCLUSION_CATEGORY_UNKNOWN) {
    return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was largely traced to presently unattributed service-style endpoints${caseConclusionBuildTailPhrase(others)}.`;
  }
  if (majority === CASE_CONCLUSION_CATEGORY_DORMANT) {
    return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was largely traced to dormant wallets${caseConclusionBuildTailPhrase(others)}.`;
  }
  if (majority === CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET) {
    return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was largely traced to private wallet addresses${caseConclusionBuildTailPhrase(others)}.`;
  }
  const mix = caseConclusionBuildCategoryPhrases(state, '');
  if (mix.length <= 0) {
    return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was traced to multiple endpoints.`;
  }
  return `In this matter, approximately ${caseConclusionFormatUsd(anchorValue)} in day-of-loss value was traced to ${caseConclusionJoinHumanList(mix)}.`;
}

function caseConclusionBuildRankedCategorySentence(state = {}, categoryTag = '', lead = '') {
  const rows = (Array.isArray(state.buckets) ? state.buckets : []).filter(bucket => bucket.categoryTag === categoryTag);
  if (!rows.length) return '';
  const total = Number(state.totalTracedUsd) || 0;
  const threshold = total * 0.01;
  const major = [];
  const minor = [];
  rows.forEach((row) => {
    const amount = Number(row.usdEquivalent) || 0;
    if (amount >= threshold) major.push(`${row.name} (${row.percentageDisplay})`);
    else minor.push(row.name);
  });
  if (!major.length) {
    return `${lead.replace('the funds were traced to ', 'only portions representing less than one percent of the funds were traced to ')}${caseConclusionJoinHumanList(minor)}.`;
  }
  let sentence = `${lead}${caseConclusionJoinHumanList(major)}.`;
  if (minor.length > 0) {
    sentence += ` Additionally, portions representing less than one percent of the funds were traced to ${caseConclusionJoinHumanList(minor)}.`;
  }
  return sentence;
}

export function buildCaseConclusionText(state = {}) {
  if (!state.canBuild) return 'The conclusion cannot be built until the What Went Where step is current and reviewed.';
  if ((Number(state.totalTracedUsd) || 0) <= 0) return 'No traced destinations are currently available from the What Went Where analysis.';

  const sentences = [caseConclusionBuildLeadSentence(state)];
  const exchangeSentence = caseConclusionBuildRankedCategorySentence(
    state,
    CASE_CONCLUSION_CATEGORY_EXCHANGE,
    'In terms of exchanges, the funds were traced to '
  );
  if (exchangeSentence) sentences.push(exchangeSentence);

  const nonExchangeSentence = caseConclusionBuildRankedCategorySentence(
    state,
    CASE_CONCLUSION_CATEGORY_NON_EXCHANGE,
    'In terms of non-exchange entities, the funds were traced to '
  );
  if (nonExchangeSentence) sentences.push(nonExchangeSentence);

  if ((Number(state.privateWalletUsd) || 0) > 0) {
    sentences.push(`Some funds were also traced to private wallet addresses (${caseConclusionFormatPercent((Number(state.privateWalletUsd) || 0) / (Number(state.totalTracedUsd) || 1))}), spread across ${state.privateWalletBucketCount || 1} distinct address bucket${(state.privateWalletBucketCount || 1) === 1 ? '' : 's'}.`);
  }

  if ((Number(state.dormantUsd) || 0) > 0) {
    sentences.push(`Some funds were traced to dormant wallets (${caseConclusionFormatPercent((Number(state.dormantUsd) || 0) / (Number(state.totalTracedUsd) || 1))}) where the funds presently remain. See the Dormant Wallet table for further information about these funds.`);
  }

  if ((Number(state.unknownUsd) || 0) > 0) {
    sentences.push(`Some funds were traced to entities that had features of an unattributed service address or hot wallet, and the path was ended (${caseConclusionFormatPercent((Number(state.unknownUsd) || 0) / (Number(state.totalTracedUsd) || 1))}). These path endings reflect presently unattributed service-style endpoints where the available data did not support a more specific attribution at the time of review.`);
  }

  if ((state.unresolvedValuationCount || 0) > 0) {
    sentences.push(`${state.unresolvedValuationCount} path-ending valuation${state.unresolvedValuationCount === 1 ? '' : 's'} still require analyst review, so the conclusion should be treated as preliminary until those valuation issues are resolved.`);
  }

  return sentences.filter(Boolean).join(' ').trim();
}

export function buildCaseConclusionStatsSummary(state = {}) {
  if (!state) return 'Conclusion state is unavailable.';
  return [
    `Total estimated loss: ${state.totalEstimatedLossDisplay || caseConclusionFormatUsd(state.totalEstimatedLossUsd)}`,
    `Total traced: ${state.totalTracedDisplay || caseConclusionFormatUsd(state.totalTracedUsd)}`,
    `Exchanges: ${state.exchangeBucketCount || 0} bucket(s), ${state.exchangeDisplay || caseConclusionFormatUsd(state.exchangeUsd)}`,
    `Non-exchange entities: ${state.nonExchangeEntityBucketCount || 0} bucket(s), ${state.nonExchangeDisplay || caseConclusionFormatUsd(state.nonExchangeUsd)}`,
    `Private wallet addresses: ${state.privateWalletBucketCount || 0} bucket(s), ${state.privateWalletDisplay || caseConclusionFormatUsd(state.privateWalletUsd)}`,
    `Unknown / unattributed endpoints: ${state.unknownBucketCount || 0} bucket(s), ${state.unknownDisplay || caseConclusionFormatUsd(state.unknownUsd)}`,
    `Dormant wallets: ${state.dormantDisplay || caseConclusionFormatUsd(state.dormantUsd)}`
  ].join('\n');
}

export function buildCaseConclusionState(whatWentWhereState = {}, workflowState = {}, options = {}) {
  const canBuild = !!whatWentWhereState.canBuild && !!whatWentWhereState.reviewIsCurrent && (whatWentWhereState.generatedPathEndingCount || 0) > 0;
  const rawBuckets = buildCaseConclusionBuckets(whatWentWhereState);
  const totalEstimatedLossUsd = (Array.isArray(whatWentWhereState.referenceRows) ? whatWentWhereState.referenceRows : [])
    .filter(row => row?.isUsable && Number.isFinite(Number(row.currentUsdValue)))
    .reduce((sum, row) => sum + Number(row.currentUsdValue), 0);
  const totalTracedUsd = Number(whatWentWhereState.overallUsdTotal) || rawBuckets.reduce((sum, bucket) => sum + (Number(bucket.usdEquivalent) || 0), 0);
  const buckets = rawBuckets.map(bucket => {
    const percentageOfTraced = totalTracedUsd > 0 ? (Number(bucket.usdEquivalent) || 0) / totalTracedUsd : 0;
    return {
      ...bucket,
      percentageOfTraced,
      percentageDisplay: caseConclusionFormatPercent(percentageOfTraced),
      usdDisplay: caseConclusionFormatUsd(bucket.usdEquivalent)
    };
  });
  const exchangeUsd = caseConclusionSumBuckets(buckets, CASE_CONCLUSION_CATEGORY_EXCHANGE);
  const nonExchangeUsd = caseConclusionSumBuckets(buckets, CASE_CONCLUSION_CATEGORY_NON_EXCHANGE);
  const privateWalletUsd = caseConclusionSumBuckets(buckets, CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET);
  const unknownUsd = caseConclusionSumBuckets(buckets, CASE_CONCLUSION_CATEGORY_UNKNOWN);
  const dormantUsd = caseConclusionSumBuckets(buckets, CASE_CONCLUSION_CATEGORY_DORMANT);
  const privateWalletBucketCount = privateWalletUsd > 0
    ? Math.max(caseConclusionCountDistinctPathBuckets(whatWentWhereState, CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET), caseConclusionCountBuckets(buckets, CASE_CONCLUSION_CATEGORY_PRIVATE_WALLET), 1)
    : 0;

  const baseState = {
    rendererVersion: CASE_CONCLUSION_RENDERER_VERSION,
    canBuild,
    whatWentWhereBuildIsCurrent: !!whatWentWhereState.buildIsCurrent,
    whatWentWhereReviewIsCurrent: !!whatWentWhereState.reviewIsCurrent,
    whatWentWhereBasisHash: whatWentWhereState.currentBasisHash || '',
    mainTransactionCount: whatWentWhereState.referenceRowCount || 0,
    pathEndingCount: whatWentWhereState.generatedPathEndingCount || 0,
    unresolvedValuationCount: whatWentWhereState.valuationUnresolvedCount || 0,
    totalEstimatedLossUsd,
    totalEstimatedLossDisplay: caseConclusionFormatUsd(totalEstimatedLossUsd),
    totalTracedUsd,
    totalTracedDisplay: caseConclusionFormatUsd(totalTracedUsd),
    exchangeUsd,
    exchangeDisplay: caseConclusionFormatUsd(exchangeUsd),
    exchangeBucketCount: caseConclusionCountBuckets(buckets, CASE_CONCLUSION_CATEGORY_EXCHANGE),
    nonExchangeUsd,
    nonExchangeDisplay: caseConclusionFormatUsd(nonExchangeUsd),
    nonExchangeEntityUsd: nonExchangeUsd,
    nonExchangeEntityDisplay: caseConclusionFormatUsd(nonExchangeUsd),
    nonExchangeEntityBucketCount: caseConclusionCountBuckets(buckets, CASE_CONCLUSION_CATEGORY_NON_EXCHANGE),
    nonExchangeBucketCount: caseConclusionCountBuckets(buckets, CASE_CONCLUSION_CATEGORY_NON_EXCHANGE),
    privateWalletUsd,
    privateWalletDisplay: caseConclusionFormatUsd(privateWalletUsd),
    privateWalletBucketCount,
    unknownUsd,
    unknownDisplay: caseConclusionFormatUsd(unknownUsd),
    unknownBucketCount: caseConclusionCountBuckets(buckets, CASE_CONCLUSION_CATEGORY_UNKNOWN),
    dormantUsd,
    dormantDisplay: caseConclusionFormatUsd(dormantUsd),
    dormantBucketCount: caseConclusionCountBuckets(buckets, CASE_CONCLUSION_CATEGORY_DORMANT),
    buckets,
    bucketCount: buckets.length,
    topExchangeEntities: caseConclusionTopEntities(whatWentWhereState, 'Exchange', 3),
    topNonExchangeEntities: caseConclusionTopEntities(whatWentWhereState, 'Non-Exchange', 3),
    statsSummary: '',
    currentBasisHash: '',
    generatedConclusionText: '',
    generatedText: '',
    selectedConclusionText: '',
    selectedText: '',
    storedSelectedText: '',
    reviewIsCurrent: false,
    summary: '',
    statusLabel: ''
  };
  baseState.statsSummary = buildCaseConclusionStatsSummary(baseState);
  const generatedText = buildCaseConclusionText(baseState);
  const currentBasisHash = `case-conclusion-${caseBuddyHashString(JSON.stringify({
    renderer: CASE_CONCLUSION_RENDERER_VERSION,
    whatWentWhereBasis: baseState.whatWentWhereBasisHash,
    totalEstimatedLossUsd: baseState.totalEstimatedLossUsd,
    totalTracedUsd: baseState.totalTracedUsd,
    buckets: buckets.map(bucket => [bucket.categoryTag, bucket.name, bucket.usdEquivalent, bucket.tokensText]),
    generatedText
  }))}`;
  const review = workflowState?.caseConclusionReview || null;
  const reviewMatchesCurrentBasis = !!review?.reviewedAtUtc && review.basisHash === currentBasisHash;
  const storedSelectedText = normalizeCell(review?.selectedText);
  const selectedText = reviewMatchesCurrentBasis && storedSelectedText ? storedSelectedText : generatedText;
  const reviewIsCurrent = canBuild && reviewMatchesCurrentBasis && Number(review.revision) === Number(options.caseDataRevision || 0);
  const summary = !whatWentWhereState.canBuild
    ? 'Build What Went Where before generating the conclusion.'
    : !whatWentWhereState.reviewIsCurrent
      ? 'Review What Went Where before marking the conclusion reviewed.'
      : canBuild
        ? reviewIsCurrent
          ? 'Case conclusion is current and reviewed.'
          : 'Desktop-style case conclusion is ready to review, edit, copy, or download.'
        : 'What Went Where did not generate reportable path-ending rows yet.';
  return {
    ...baseState,
    currentBasisHash,
    generatedConclusionText: generatedText,
    generatedText,
    selectedConclusionText: selectedText,
    selectedText,
    storedSelectedText,
    reviewIsCurrent,
    summary,
    statusLabel: reviewIsCurrent ? 'Current · reviewed' : canBuild ? 'Ready' : 'Needs What Went Where'
  };
}

export function buildCaseConclusionPreviewRows(state = {}, text = '') {
  const conclusionText = normalizeCell(text) || state.selectedText || state.generatedText || '';
  return [
    ['Case Buddy Conclusion Preview', '', '', '', '', '', '', '', '', 'Preview/audit only. Built from reviewed What Went Where data.'],
    ['Renderer', state.rendererVersion || '', 'Basis', state.currentBasisHash || '', 'What Went Where Basis', state.whatWentWhereBasisHash || ''],
    ['Estimated Loss', state.totalEstimatedLossDisplay || '', 'Traced Value', state.totalTracedDisplay || '', 'Path Endings', state.pathEndingCount || 0, 'Unresolved Valuations', state.unresolvedValuationCount || 0],
    [],
    ...String(conclusionText || '').split('\n').map(line => [line]),
    [],
    ['Supporting Buckets'],
    ['Category', 'Name', 'USD Total', 'Percent Of Traced', 'Token Breakdown', 'Unresolved Rows'],
    ...(Array.isArray(state.buckets) ? state.buckets : []).map(row => [row.displayCategory || row.category, row.name, row.usdDisplay, row.percentageDisplay, row.tokensText, row.unresolvedCount || 0]),
    [],
    ['Stats Summary'],
    ...String(state.statsSummary || '').split('\n').map(line => [line])
  ];
}

export const CASE_REPORT_BODY_VARIANT_STANDARD = 'standard';

export const CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING = 'custom_spacing';

export const CASE_REPORT_BODY_RENDERER_VERSION = 'case_report_body_renderer_web_v3';

export const CASE_REPORT_BODY_COLOR_ORANGE = '#fec013';

export const CASE_REPORT_BODY_COLOR_BLUE = '#55c8fc';

export const CASE_REPORT_BODY_COLOR_PURPLE = '#9900FF';

export const CASE_REPORT_BODY_COLOR_GRAY = 'gray';

export const CASE_REPORT_BODY_PURPLE_TERMS = Object.freeze([
  'Thorchain', 'Pendle', 'Bustabit', 'Gamdom', 'Rollbit', 'mimic.fi', 'MetaMask', 'Airswap', 'Rango',
  'UniswapX', 'Curve Swap', 'Tokenlon', 'Roobet', 'Stake', 'Stargate: Pool Native', 'Telegram Black U',
  'Stargate: Pool USDT', 'Stargate Finance', 'Stargate', 'Uniswap', 'SunSwap', 'Wormhole', 'OKXWeb3',
  'LiFi', 'Li.Fi', 'LayerZero', 'Chainflip', 'BKBridge', 'Bridgers', '0xProtocol', 'Hop Protocol',
  'PancakeSwap', '1inch', 'Rizzolver', 'SushiSwap', 'SWFT Blockchain', 'SWFT', 'TransitSwap',
  'OKX Web 3', 'Transit Finance'
]);

export const CASE_REPORT_BODY_TERMINAL_RULER_TERMS = Object.freeze(['hot wallet', 'path ends', 'funds remain', 'funds sit', 'stopped', 'ended']);

export function caseReportBodyHtmlEncode(value = '') {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function caseReportBodyNormalizeWhitespace(value = '') {
  return normalizeCell(value).replace(/\s+/g, ' ').trim();
}

export function caseReportBodyContainsAny(value = '', terms = []) {
  const text = normalizeCell(value).toLowerCase();
  return (Array.isArray(terms) ? terms : []).some(term => text.includes(normalizeCell(term).toLowerCase()));
}

export function caseReportBodyBuildLegendLookup(legendAnalysis = {}) {
  const lookup = new Map();
  getCaseTemplateLegendEntries(legendAnalysis).forEach((entry) => {
    const address = normalizeCell(entry.address || entry.Address || entry.preferredDisplayAddress);
    const label = normalizeCell(entry.label || entry.Label || entry.proposedLegendLabel || entry.displayLabel);
    const key = normalizeCaseAddressLegendAddressKey(address);
    if (key && label && !lookup.has(key)) lookup.set(key, label);
  });
  return lookup;
}

export function caseReportBodyBuildKeywordList(effectiveCurator = {}) {
  const seen = new Set();
  const exchange = [];
  const nonExchange = [];
  const add = (target, value) => {
    const text = normalizeCell(value);
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(`${target}:${key}`)) return;
    seen.add(`${target}:${key}`);
    if (target === 'exchange') exchange.push(text);
    else nonExchange.push(text);
  };
  (effectiveCurator?.exchange || []).forEach(value => add('exchange', value));
  (effectiveCurator?.nonExchange || []).forEach(value => add('nonExchange', value));
  return { exchange, nonExchange };
}

export function caseReportBodyResolveAddressLabel(legendLookup = new Map(), address = '', fallback = '') {
  const key = normalizeCaseAddressLegendAddressKey(address);
  if (key && legendLookup.has(key)) return legendLookup.get(key) || fallback;
  return fallback;
}

export function caseReportBodyTryExtractEntityBeforeAddressMarker(label = '', ownerPrefix = '') {
  const text = normalizeCell(label);
  const prefix = normalizeCell(ownerPrefix);
  if (!text || !prefix) return '';
  const prefixIndex = text.toLowerCase().indexOf(prefix.toLowerCase());
  const addressIndex = text.toLowerCase().indexOf('address #');
  if (prefixIndex < 0 || addressIndex < 0 || addressIndex <= prefixIndex + prefix.length) return '';
  return caseReportBodyNormalizeWhitespace(text.slice(prefixIndex + prefix.length, addressIndex));
}

export function caseReportBodyTryExtractEntityBeforePhrase(label = '', phrase = '') {
  const text = normalizeCell(label);
  const needle = normalizeCell(phrase);
  if (!text || !needle) return '';
  const index = text.toLowerCase().indexOf(needle.toLowerCase());
  if (index <= 0) return '';
  return caseReportBodyNormalizeWhitespace(text.slice(0, index));
}

export function caseReportBodyEvaluatePartyStyle(label = '', effectiveCurator = {}) {
  const text = normalizeCell(label);
  const keywords = caseReportBodyBuildKeywordList(effectiveCurator);
  const result = { colorHex: CASE_REPORT_BODY_COLOR_ORANGE, entitySuffix: '', useBold: true };
  if (!text) return result;

  const entityFromOwner = caseReportBodyTryExtractEntityBeforeAddressMarker(text, "Suspect's")
    || caseReportBodyTryExtractEntityBeforeAddressMarker(text, 'Suspects')
    || caseReportBodyTryExtractEntityBeforeAddressMarker(text, "Victim's")
    || caseReportBodyTryExtractEntityBeforeAddressMarker(text, 'Victims');
  if (entityFromOwner) {
    result.colorHex = CASE_REPORT_BODY_COLOR_BLUE;
    result.entitySuffix = entityFromOwner;
    return result;
  }

  const hotWalletEntity = caseReportBodyTryExtractEntityBeforePhrase(text, 'Hot Wallet')
    || caseReportBodyTryExtractEntityBeforePhrase(text, 'Hot wallet');
  if (hotWalletEntity) {
    result.colorHex = CASE_REPORT_BODY_COLOR_BLUE;
    result.entitySuffix = hotWalletEntity;
    return result;
  }

  const depositEntity = caseReportBodyTryExtractEntityBeforePhrase(text, 'Deposit Address')
    || caseReportBodyTryExtractEntityBeforePhrase(text, 'Deposit Wallet')
    || caseReportBodyTryExtractEntityBeforePhrase(text, 'Deposit Account');
  if (depositEntity) {
    result.colorHex = CASE_REPORT_BODY_COLOR_BLUE;
    result.entitySuffix = depositEntity;
    return result;
  }

  if (caseReportBodyContainsAny(text, keywords.exchange)) {
    result.colorHex = CASE_REPORT_BODY_COLOR_BLUE;
    result.useBold = true;
  }
  if (caseReportBodyContainsAny(text, keywords.nonExchange) || caseReportBodyContainsAny(text, CASE_REPORT_BODY_PURPLE_TERMS)) {
    result.colorHex = CASE_REPORT_BODY_COLOR_PURPLE;
    result.useBold = false;
  }
  return result;
}

export function caseReportBodyBuildParty(legendLookup = new Map(), address = '', fallback = '', effectiveCurator = {}) {
  const label = caseReportBodyResolveAddressLabel(legendLookup, address, fallback);
  const style = caseReportBodyEvaluatePartyStyle(label, effectiveCurator);
  return {
    address: normalizeCell(address),
    label,
    summaryLabel: label,
    colorHex: style.colorHex || CASE_REPORT_BODY_COLOR_ORANGE,
    entitySuffix: style.entitySuffix || '',
    useBold: style.useBold !== false
  };
}

export function caseReportBodyFormatAmount(amount = '', token = '') {
  const tokenText = normalizeCell(token);
  if (!tokenText) return '';
  const numeric = Number.parseFloat(normalizeCell(amount).replace(/,/g, ''));
  if (!Number.isFinite(numeric)) return '';
  const upper = tokenText.toUpperCase();
  const fractionDigits = upper === 'USDT' || upper === 'USDC' || upper === 'USDD' || upper === 'DAI'
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : upper === 'BTC' || upper === 'WBTC'
      ? { minimumFractionDigits: 0, maximumFractionDigits: 8 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 4 };
  return `${numeric.toLocaleString('en-US', fractionDigits)} ${tokenText}`;
}

export function caseReportBodyBuildMovementNotes(row = []) {
  const systemNotes = normalizeCell(row?.[9]);
  const analystNotes = normalizeCell(row?.[11]);
  if (!systemNotes) return analystNotes;
  if (!analystNotes) return systemNotes;
  if (systemNotes.toLowerCase() === analystNotes.toLowerCase()) return systemNotes;
  return `${systemNotes} ${analystNotes}`;
}

export function caseReportBodyBuildMovement(row = [], legendLookup = new Map(), effectiveCurator = {}) {
  const fromAddress = normalizeCell(row?.[2]);
  const toAddress = normalizeCell(row?.[3]);
  const amountDisplay = caseReportBodyFormatAmount(row?.[5], row?.[6]);
  const notes = caseReportBodyBuildMovementNotes(row);
  if (!fromAddress && !toAddress && !amountDisplay && !notes) return null;
  return {
    from: caseReportBodyBuildParty(legendLookup, fromAddress, 'Unknown Sender', effectiveCurator),
    to: caseReportBodyBuildParty(legendLookup, toAddress, 'Unknown Receiver', effectiveCurator),
    amountDisplay,
    notes
  };
}

export function caseReportBodyEntryHasFromHash(entry = {}) {
  return (entry.rows || []).some(item => normalizeCell(item?.row?.[14]));
}

export function caseReportBodyGetEntryDateText(entry = {}) {
  for (const item of (entry.rows || [])) {
    const value = normalizeCell(item?.row?.[4]);
    if (value) return value;
  }
  return '(date missing)';
}

export function caseReportBodyBuildEntryDocument(entry = {}, legendLookup = new Map(), effectiveCurator = {}, isTopLevel = false) {
  const lead = entry.rows?.[0]?.row || [];
  const pathLabel = firstNonEmptyValue(lead?.[0], entry.entryPathLabel, '(path)');
  const document = {
    key: `report-entry-${entry.startRow}-${normalizeComparisonKey(entry.hash)}`,
    pathLabel,
    isTopLevel,
    transactionHash: normalizeCell(entry.hash || lead?.[1]),
    dateText: caseReportBodyGetEntryDateText(entry),
    amountDisplay: '',
    summaryFromLabel: '',
    summaryToLabel: '',
    appendTerminalRuler: false,
    movements: []
  };
  let multipleFrom = false;
  let multipleTo = false;

  (entry.rows || []).forEach((item, rowOffset) => {
    const row = item?.row || [];
    if (!rowHasMeaningfulData(row)) return;
    const movement = caseReportBodyBuildMovement(row, legendLookup, effectiveCurator);
    if (!movement) return;
    document.movements.push(movement);
    if (rowOffset > 0 && movement.from.address) multipleFrom = true;
    if (rowOffset > 0 && movement.to.address) multipleTo = true;
    if (!document.amountDisplay && movement.amountDisplay) document.amountDisplay = movement.amountDisplay;
    if (caseReportBodyContainsAny(movement.notes, CASE_REPORT_BODY_TERMINAL_RULER_TERMS)) document.appendTerminalRuler = true;
  });

  if (!document.movements.length) return null;
  const first = document.movements[0];
  document.summaryFromLabel = multipleFrom ? 'Multiple Addresses' : firstNonEmptyValue(first.from.summaryLabel, first.from.label, 'Unknown Sender');
  document.summaryToLabel = multipleTo ? 'Multiple Addresses' : firstNonEmptyValue(first.to.summaryLabel, first.to.label, 'Unknown Receiver');
  return document;
}

export function caseReportBodyBuildDocument(mainData = [], legendLookup = new Map(), effectiveCurator = {}, options = {}) {
  const entries = parseCaseEntries(normalizeCaseMatrix(mainData));
  const sections = [];
  let currentSection = null;
  let entryCount = 0;
  let topLevelPathCount = 0;

  entries.forEach((entry, index) => {
    if (!entry?.hash || !Array.isArray(entry.rows) || entry.rows.length === 0) return;
    const isTopLevel = !caseReportBodyEntryHasFromHash(entry) || !currentSection;
    const entryDocument = caseReportBodyBuildEntryDocument(entry, legendLookup, effectiveCurator, isTopLevel);
    if (!entryDocument) return;
    entryCount += 1;
    if (isTopLevel || !currentSection) {
      topLevelPathCount += 1;
      currentSection = {
        key: `report-section-${entry.startRow}-${index}-${normalizeComparisonKey(entryDocument.pathLabel)}`,
        pathLabel: entryDocument.pathLabel,
        entries: []
      };
      sections.push(currentSection);
    }
    currentSection.entries.push(entryDocument);
  });

  return { caseId: normalizeCell(options.caseId), sections, entryCount, topLevelPathCount };
}

export function caseReportBodyPlainSectionSeparator(useCustomSpacingTokens = false) {
  return useCustomSpacingTokens ? '\n[[CUSTOM_SPACING]]\n[[CUSTOM_SPACING]]\n' : '\n\n\n';
}

export function caseReportBodyPlainEntrySeparator(useCustomSpacingTokens = false) {
  return useCustomSpacingTokens ? '\n[[CUSTOM_SPACING]]\n' : '\n\n';
}

export function caseReportBodyHeaderSpacing(pathLabel = '') {
  return normalizeCell(pathLabel).length > 1 ? '\t' : '\t\t';
}

export function caseReportBodyAppendPlainSection(lines = [], section = {}, useCustomSpacingTokens = false) {
  const pathLabel = firstNonEmptyValue(section.pathLabel, '(path)');
  lines.push(`Transaction Path #${pathLabel}${caseReportBodyHeaderSpacing(pathLabel)}Subpoenable Entity Path`);
  lines.push('');
  lines.push('(Add figure label name)');
  lines.push('(Insert figure here)');
  if (useCustomSpacingTokens) lines.push('[[CUSTOM_SPACING]]');
  else lines.push('');
}

export function caseReportBodyAppendPlainMovement(lines = [], movement = {}, useCustomSpacingTokens = false) {
  lines.push(`From ${firstNonEmptyValue(movement.from?.label, 'Unknown Sender')}${movement.from?.entitySuffix ? `    ${movement.from.entitySuffix}` : ''}`);
  if (movement.from?.address) lines.push(`    ${movement.from.address}`);
  if (useCustomSpacingTokens) lines.push('[[CUSTOM_SPACING]]');
  else lines.push('');
  lines.push(`To ${firstNonEmptyValue(movement.to?.label, 'Unknown Receiver')}${movement.to?.entitySuffix ? `    ${movement.to.entitySuffix}` : ''}${movement.amountDisplay ? `       Amount: ${movement.amountDisplay}` : ''}`);
  if (movement.to?.address) lines.push(`    ${movement.to.address}`);
  if (movement.notes) {
    if (useCustomSpacingTokens) lines.push('[[CUSTOM_SPACING]]');
    else lines.push('');
    lines.push(`Additional notes: ${movement.notes}`);
  }
}

export function caseReportBodyAppendPlainEntry(lines = [], entry = {}, useCustomSpacingTokens = false) {
  lines.push(`Transaction date and time: ${firstNonEmptyValue(entry.dateText, '(date missing)')}`);
  lines.push(`TXN ID #${firstNonEmptyValue(entry.pathLabel, '(path)')} - from ${firstNonEmptyValue(entry.summaryFromLabel, 'Unknown Sender')} to ${firstNonEmptyValue(entry.summaryToLabel, 'Unknown Receiver')}`);
  lines.push(`    ${normalizeCell(entry.transactionHash)}`);
  (entry.movements || []).forEach((movement, index) => {
    caseReportBodyAppendPlainMovement(lines, movement, useCustomSpacingTokens);
    if (index < (entry.movements || []).length - 1) {
      if (useCustomSpacingTokens) lines.push('', '[[CUSTOM_SPACING]]');
      else lines.push('');
    }
  });
}

export function caseReportBodyBuildPartyHtml(party = {}) {
  const color = caseReportBodyHtmlEncode(firstNonEmptyValue(party.colorHex, CASE_REPORT_BODY_COLOR_ORANGE));
  const label = caseReportBodyHtmlEncode(firstNonEmptyValue(party.label, 'Unknown'));
  const tagOpen = party.useBold === false ? '' : '<b>';
  const tagClose = party.useBold === false ? '' : '</b>';
  let html = `<span style="color: ${color};">${tagOpen}${label}${tagClose}</span>`;
  if (party.entitySuffix) {
    html += `<b>    <span style="color: ${color};">${caseReportBodyHtmlEncode(party.entitySuffix)}</span></b>`;
  }
  return html;
}

export function caseReportBodyBuildSummaryHtml(label = '', color = CASE_REPORT_BODY_COLOR_ORANGE) {
  return `<span style="color: ${caseReportBodyHtmlEncode(color)};"><b>${caseReportBodyHtmlEncode(firstNonEmptyValue(label, 'Unknown'))}</b></span>`;
}

export function caseReportBodyBuildAddressHtml(address = '') {
  return `<span style="color: ${CASE_REPORT_BODY_COLOR_GRAY}; font-style: italic;">${caseReportBodyHtmlEncode(address)}</span>`;
}

export function caseReportBodyBuildHashAnchorHtml(hash = '') {
  const text = normalizeCell(hash);
  if (!text) return '';
  return `<a href="https://www.oklink.com/multi-search#key=${caseReportBodyHtmlEncode(text)}">${caseReportBodyHtmlEncode(text)}</a>`;
}

export function caseReportBodyHighlightNotesHtml(notes = '') {
  let encoded = caseReportBodyHtmlEncode(notes).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  CASE_REPORT_BODY_PURPLE_TERMS.forEach((term) => {
    if (!term) return;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    encoded = encoded.replace(new RegExp(escaped, 'gi'), match => `<span style="color: ${CASE_REPORT_BODY_COLOR_PURPLE};">${match}</span>`);
  });
  return encoded.replace(/\n/g, '<br>');
}

export function caseReportBodyHtmlSmallSpacer() {
  return '<div style="height: 0.5em; line-height: 0.5em; font-size: 5px;">&nbsp;</div>';
}

export function caseReportBodyHtmlTinySpacer() {
  return '<div style="height: 0.5em; line-height: 0.5em; font-size: 5px;"></div>';
}

export function caseReportBodyBuildMovementHtml(movement = {}, useCustomSpacingTokens = false) {
  let html = '';
  html += `From ${caseReportBodyBuildPartyHtml(movement.from)}<br>`;
  if (movement.from?.address) html += `&nbsp;&nbsp;&nbsp;&nbsp;${caseReportBodyBuildAddressHtml(movement.from.address)}`;
  html += useCustomSpacingTokens ? '[[CUSTOM_SPACING]]' : `${caseReportBodyHtmlSmallSpacer()}${caseReportBodyHtmlSmallSpacer()}`;
  html += `To ${caseReportBodyBuildPartyHtml(movement.to)}`;
  if (movement.amountDisplay) html += ` &nbsp;&nbsp;Amount: <b>${caseReportBodyHtmlEncode(movement.amountDisplay)}</b>`;
  html += caseReportBodyHtmlTinySpacer();
  if (movement.to?.address) html += `&nbsp;&nbsp;&nbsp;&nbsp;${caseReportBodyBuildAddressHtml(movement.to.address)}`;
  if (movement.notes) {
    if (useCustomSpacingTokens) html += `[[CUSTOM_SPACING]]<br><b><i>Additional notes:</i></b> ${caseReportBodyHighlightNotesHtml(movement.notes)}[[CUSTOM_SPACING]]`;
    else html += `${caseReportBodyHtmlSmallSpacer()}<b><i>Additional notes:</i></b> ${caseReportBodyHighlightNotesHtml(movement.notes)}${caseReportBodyHtmlSmallSpacer()}`;
  } else if (!useCustomSpacingTokens) {
    html += caseReportBodyHtmlSmallSpacer();
  }
  return html;
}

export function caseReportBodyBuildEntryHtml(entry = {}, useCustomSpacingTokens = false) {
  const first = (entry.movements || [])[0] || {};
  const fromColor = first.from?.colorHex || CASE_REPORT_BODY_COLOR_ORANGE;
  const toColor = first.to?.colorHex || CASE_REPORT_BODY_COLOR_ORANGE;
  let html = '<div style="font-family: Arial; font-size: 14.5px; color: black;">';
  html += `Transaction date and time:<strong> ${caseReportBodyHtmlEncode(firstNonEmptyValue(entry.dateText, '(date missing)'))}</strong><br>`;
  html += `<font style="font-size: 18px;"><strong>TXN ID <font style="color: red;">#${caseReportBodyHtmlEncode(firstNonEmptyValue(entry.pathLabel, '(path)'))}</font></strong> - from ${caseReportBodyBuildSummaryHtml(entry.summaryFromLabel, fromColor)} to ${caseReportBodyBuildSummaryHtml(entry.summaryToLabel, toColor)}</font>`;
  html += `\n&nbsp;&nbsp;&nbsp;&nbsp;${caseReportBodyBuildHashAnchorHtml(entry.transactionHash)}`;
  html += useCustomSpacingTokens ? '[[CUSTOM_SPACING]]' : `${caseReportBodyHtmlSmallSpacer()}${caseReportBodyHtmlSmallSpacer()}`;
  html += caseReportBodyHtmlTinySpacer();
  (entry.movements || []).forEach((movement, index) => {
    html += caseReportBodyBuildMovementHtml(movement, useCustomSpacingTokens);
    if (index < (entry.movements || []).length - 1) html += useCustomSpacingTokens ? '[[CUSTOM_SPACING]]' : '<br>';
  });
  if (entry.appendTerminalRuler) html += '<hr>';
  if (!useCustomSpacingTokens) html += '<br>';
  html += '</div>';
  return html;
}

export function caseReportBodySectionEndsWithTerminalRuler(section = {}) {
  const entries = Array.isArray(section?.entries) ? section.entries.filter(Boolean) : [];
  const lastEntry = entries.length ? entries[entries.length - 1] : null;
  return !!lastEntry?.appendTerminalRuler;
}

export function caseReportBodyFlowBoundaryRulers(previousSection = {}) {
  return caseReportBodySectionEndsWithTerminalRuler(previousSection) ? '<hr>' : '<hr><hr>';
}

export function caseReportBodyBuildSectionHeaderHtml(section = {}, options = {}) {
  const pathLabel = firstNonEmptyValue(section.pathLabel, '(path)');
  const pageBreakStyle = options.pageBreakBefore
    ? ' page-break-before: always; break-before: page; mso-page-break-before: always;'
    : '';
  return `<div class="case-report-path-heading${options.pageBreakBefore ? ' case-report-path-heading--page-break' : ''}" style="font-family: Arial; font-size: 26.5px; color: black; margin-top: 20px; page-break-after: avoid; break-after: avoid;${pageBreakStyle}">Transaction Path #${caseReportBodyHtmlEncode(pathLabel)}${caseReportBodyHeaderSpacing(pathLabel)}Subpoenable Entity Path</div><br><div style="font-family: Arial; font-size: 14.5px; color: black; page-break-inside: avoid; break-inside: avoid;">(Add figure label name)<br>(Insert figure here)<br></div>`;
}

export function caseReportBodyWrapHtmlDocument(fragment = '') {
  if (!fragment) return '';
  return `<html>\r\n<body>\r\n<!--StartFragment-->${fragment}<!--EndFragment-->\r\n</body>\r\n</html>`;
}

export function caseReportBodyRenderDocument(document = {}, useCustomSpacingTokens = false) {
  if (!document?.sections?.length) {
    return {
      variantId: useCustomSpacingTokens ? CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING : CASE_REPORT_BODY_VARIANT_STANDARD,
      displayName: useCustomSpacingTokens ? 'Tool38H custom spacing' : 'Tool38G standard',
      plainText: 'No main transaction entries were found.',
      htmlFragment: '',
      htmlDocument: '',
      previewText: 'No main transaction entries were found.'
    };
  }
  const plainParts = [];
  let html = '';
  document.sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      const previousSection = document.sections[sectionIndex - 1] || {};
      plainParts.push(caseReportBodyPlainSectionSeparator(useCustomSpacingTokens));
      html += caseReportBodyFlowBoundaryRulers(previousSection);
    }
    const sectionLines = [];
    caseReportBodyAppendPlainSection(sectionLines, section, useCustomSpacingTokens);
    (section.entries || []).forEach((entry, entryIndex) => {
      if (entryIndex > 0) sectionLines.push(caseReportBodyPlainEntrySeparator(useCustomSpacingTokens));
      caseReportBodyAppendPlainEntry(sectionLines, entry, useCustomSpacingTokens);
    });
    plainParts.push(sectionLines.join('\n'));
    html += caseReportBodyBuildSectionHeaderHtml(section, { pageBreakBefore: sectionIndex > 0 });
    (section.entries || []).forEach((entry) => { html += caseReportBodyBuildEntryHtml(entry, useCustomSpacingTokens); });
  });
  if (html) html += caseReportBodyFlowBoundaryRulers(document.sections[document.sections.length - 1] || {});
  const plainText = plainParts.join('').replace(/[\r\n]+$/g, '');
  return {
    variantId: useCustomSpacingTokens ? CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING : CASE_REPORT_BODY_VARIANT_STANDARD,
    displayName: useCustomSpacingTokens ? 'Tool38H custom spacing' : 'Tool38G standard',
    usesCustomSpacingTokens: useCustomSpacingTokens,
    plainText,
    htmlFragment: html,
    htmlDocument: caseReportBodyWrapHtmlDocument(html),
    previewText: plainText
  };
}

export function buildCaseReportBodyState(mainData = [], effectiveCurator = {}, workflowState = {}, options = {}) {
  const legendAnalysis = options.legendAnalysis || buildCaseAddressLegendAnalysis(mainData, effectiveCurator, workflowState, {});
  const legendLookup = caseReportBodyBuildLegendLookup(legendAnalysis);
  const document = caseReportBodyBuildDocument(mainData, legendLookup, effectiveCurator, { caseId: options.caseId });
  const standard = caseReportBodyRenderDocument(document, false);
  const customSpacing = caseReportBodyRenderDocument(document, true);
  const legendBuildIsCurrent = !!legendAnalysis.buildIsCurrent;
  const legendReviewIsCurrent = !!legendAnalysis.reviewIsCurrent;
  const canBuild = legendBuildIsCurrent && legendReviewIsCurrent && document.entryCount > 0;
  const currentBasisHash = `report-body-${caseBuddyHashString(JSON.stringify({
    renderer: CASE_REPORT_BODY_RENDERER_VERSION,
    caseId: normalizeCell(options.caseId),
    legendBasis: legendAnalysis.currentBasisHash || '',
    standardText: standard.plainText,
    customText: customSpacing.plainText,
    standardHtml: standard.htmlFragment,
    customHtml: customSpacing.htmlFragment
  }))}`;
  const review = workflowState?.reportBodyReview || null;
  const reviewIsCurrent = canBuild && !!review?.reviewedAtUtc && review.basisHash === currentBasisHash && Number(review.revision) === Number(options.caseDataRevision || 0);
  return {
    caseId: normalizeCell(options.caseId),
    rendererVersion: CASE_REPORT_BODY_RENDERER_VERSION,
    legendBuildIsCurrent,
    legendReviewIsCurrent,
    legendEntryCount: legendAnalysis.legendEntryCount || 0,
    entryCount: document.entryCount,
    topLevelPathCount: document.topLevelPathCount,
    sections: (document.sections || []).map(section => ({ ...section, entryCount: (section.entries || []).length, previewText: (section.entries || []).slice(0, 3).map(entry => `${entry.pathLabel}: ${entry.summaryFromLabel} → ${entry.summaryToLabel}`).join(' · ') })),
    document,
    standard,
    customSpacing,
    generatedText: standard.plainText,
    customSpacingText: customSpacing.plainText,
    standardHtmlFragment: standard.htmlFragment,
    standardHtmlDocument: standard.htmlDocument,
    customSpacingHtmlFragment: customSpacing.htmlFragment,
    customSpacingHtmlDocument: customSpacing.htmlDocument,
    currentBasisHash,
    canBuild,
    buildIsCurrent: canBuild,
    reviewIsCurrent,
    statsSummary: [
      `Top-level transaction paths: ${document.topLevelPathCount}`,
      `Transaction entries rendered: ${document.entryCount}`,
      `Legend build current: ${legendBuildIsCurrent ? 'Yes' : 'No'}`,
      `Legend review current: ${legendReviewIsCurrent ? 'Yes' : 'No'}`,
      `Renderer version: ${CASE_REPORT_BODY_RENDERER_VERSION}`
    ].join('\n'),
    summary: !legendBuildIsCurrent
      ? 'The report body cannot be built until the address legend build is current.'
      : !legendReviewIsCurrent
        ? 'The report body cannot be built until the address legend review is current.'
        : document.entryCount <= 0
          ? 'No main transaction entries were found to build the report body.'
          : reviewIsCurrent
            ? `Report body is current and reviewed for ${document.entryCount} transaction entr${document.entryCount === 1 ? 'y' : 'ies'}.`
            : `Report body is ready from ${document.topLevelPathCount} top-level path${document.topLevelPathCount === 1 ? '' : 's'} and ${document.entryCount} transaction entr${document.entryCount === 1 ? 'y' : 'ies'}. Review/copy it, then mark reviewed.`
  };
}

export function buildCaseReportBodyAuditRows(state = {}, actionLabel = 'Report body review') {
  return [
    ['Case Buddy Report Body Audit', '', '', '', '', '', '', '', '', 'Audit only. Report body is generated from current Main Data and the reviewed Address Legend.'],
    ['Action', actionLabel, 'Entry Count', state.entryCount || 0, 'Top-Level Paths', state.topLevelPathCount || 0, 'Legend Entries', state.legendEntryCount || 0, 'Basis', state.currentBasisHash || ''],
    ['Status', state.summary || '', 'Build Current', state.buildIsCurrent ? 'Yes' : 'No', 'Review Current', state.reviewIsCurrent ? 'Yes' : 'No', 'Renderer', state.rendererVersion || ''],
    [],
    ['Stats'],
    ...String(state.statsSummary || '').split('\n').map(line => [line])
  ];
}

export function buildCaseReportBodyPreviewRows(state = {}, variantId = CASE_REPORT_BODY_VARIANT_STANDARD) {
  const isCustom = variantId === CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING;
  const text = isCustom ? state.customSpacingText : state.generatedText;
  return [
    ['Case Buddy Report Body Preview', isCustom ? 'Tool38H custom-spacing variant' : 'Tool38G standard variant', '', '', '', '', '', '', '', 'Preview only. Use Copy Rich for rich clipboard output when available.'],
    ['Renderer', state.rendererVersion || '', 'Entry Count', state.entryCount || 0, 'Top-Level Paths', state.topLevelPathCount || 0, 'Basis', state.currentBasisHash || ''],
    [],
    ...String(text || '').split('\n').map(line => [line])
  ];
}
